import React, { useState, useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { useDispatch } from 'react-redux';
import { fetchTerminals } from './store/terminalsSlice';
import { BrowserRouter } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { AuthContext }  from './context/AuthContext'
import { PhotoProvider } from './context/PhotoContext'
import { useAuth } from './hooks/auth.hook'

import './App.css';
import './assets/scss/style.scss';
import { AppRoutes } from './routes';

// defaultTheme
import { ThemeProvider } from '@mui/material/styles';
import themes from './themes';

import config from './config';
import PushPermissionDialog from "./components/PushPermissionDialog";
import Popup from "./components/popup";
import TokenRefresh from "./components/TokenRefresh";
import ErrorBoundary from "./components/ErrorBoundary";
import { MEDIA_MANAGER_ROLE } from './utils/access';

const isNativeApp = () => Capacitor.isNativePlatform();

const isDesktopInstallSurface = () => (
  isNativeApp() ||
  (
    window.matchMedia &&
    window.matchMedia('(hover: hover) and (pointer: fine)').matches
  )
);

function App() {
  const {authData, login, logout} = useAuth();
  const isAuthenticated = !!authData.token;
// console.log('authData:', authData)
// console.log('login:', login)
// console.log('logout:', logout)
// console.log('isAuthenticated:', isAuthenticated)
  const customization = useSelector((state) => state.customization);

  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallButton, setShowInstallButton] = useState(false);

  const dispatch = useDispatch();
  useEffect(() => {
    if (!authData.ready || Number(authData.userTypeId) === MEDIA_MANAGER_ROLE) return;
    dispatch(fetchTerminals());
  }, [dispatch, authData.ready, authData.userTypeId]);

  useEffect(() => {
    if (isNativeApp()) {
      setDeferredPrompt(null);
      setShowInstallButton(false);
      return undefined;
    }

    function beforeInstallPromptHandler(e) {
      e.preventDefault();
      if (isDesktopInstallSurface()) {
        setDeferredPrompt(null);
        setShowInstallButton(false);
        return;
      }
      setDeferredPrompt(e);
      setShowInstallButton(true);
    }

    window.addEventListener('beforeinstallprompt', beforeInstallPromptHandler);

    return () => {
      window.removeEventListener('beforeinstallprompt', beforeInstallPromptHandler);
    };
  }, []);

  useEffect(() => {
    if (isNativeApp()) {
      setShowInstallButton(false);
      return undefined;
    }

    if (!window.matchMedia) {
      return undefined;
    }

    const desktopMedia = window.matchMedia('(hover: hover) and (pointer: fine)');
    const handleDesktopChange = (e) => {
      setShowInstallButton(!e.matches && Boolean(deferredPrompt));
    };

    if (desktopMedia.matches) {
      setShowInstallButton(false);
    }

    desktopMedia.addEventListener('change', handleDesktopChange);

    return () => {
      desktopMedia.removeEventListener('change', handleDesktopChange);
    };
  }, [deferredPrompt]);

  const handleInstallClick = () => {
    if (!deferredPrompt) {
      return;
    }
    deferredPrompt.prompt();

    deferredPrompt.userChoice.then(choiceResult => {
      if (choiceResult.outcome === 'accepted') {
        console.log('User installed the app');
      } else {
        console.log('User dismissed the installation');
      }
      setDeferredPrompt(null);
      setShowInstallButton(false);
    });
  };

  // iOS install banner
  const isIos = () => {
    const ua = window.navigator.userAgent.toLowerCase();
    return /iphone|ipad|ipod/.test(ua);
  };
  
  const isInStandaloneMode = () =>
    'standalone' in window.navigator && window.navigator.standalone;
  
  const [showIosInstallBanner, setShowIosInstallBanner] = useState(false);
  
  useEffect(() => {
    if (!isNativeApp() && isIos() && !isInStandaloneMode()) {
      setShowIosInstallBanner(true);
    }
  }, []);

  const vapidPublicKey = config.VAPID_PUBLIC_KEY;
  function urlBase64ToUint8Array(base64String) {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i)
      outputArray[i] = rawData.charCodeAt(i);
    return outputArray;
  }
  async function registerAndSubscribe() {
    if (isNativeApp()) return;
    if (!authData.token || Number(authData.userTypeId) === MEDIA_MANAGER_ROLE) return;
    if ("serviceWorker" in navigator && "PushManager" in window) {
      try {
        const registration = await navigator.serviceWorker.register("/pwabuilder-sw.js");
        // Wait until there is an active service worker controlling the page
        const readyRegistration = registration.active
          ? registration
          : await navigator.serviceWorker.ready;
        const sub = await readyRegistration.pushManager.getSubscription();

      // console.log('Subscription:', sub);
      // console.log("authData:", authData);

      // Check for waiting service worker and activate it
      if (registration.waiting) {
        console.log('Found waiting service worker, activating...');
        registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        // Reload after a short delay to activate the new service worker
        setTimeout(() => {
          window.location.reload();
        }, 1000);
        return;
      }

      // Listen for service worker updates
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        if (newWorker) {
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              console.log('New service worker available, reloading...');
              newWorker.postMessage({ type: 'SKIP_WAITING' });
              setTimeout(() => {
                window.location.reload();
              }, 1000);
            }
          });
        }
      });

      if (!sub) {
        const subscription = await readyRegistration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
        });
        // console.log('New subscription:', subscription);
        // send to the server
        const res = await fetch(`${config.API_URL}api/subscribe`, {
        // await fetch(`${config.API_URL}/subscribe`, {
          method: "POST",
          headers: { 
            "Content-Type": "application/json"
          },
          credentials: 'include',
          body: JSON.stringify({ subscription, userId: authData.userId })
        });
        console.log("User subscribed!", await res.json());
      } else {
        // Verify subscription is still valid and sync with server
        // console.log('Already subscribed!', sub);
        // console.log('Subscription endpoint:', sub.endpoint);
        
        // PushSubscription object has a toJSON method that properly serializes keys
        // But we need to check if keys are actually present
        const subscriptionJSON = sub.toJSON ? sub.toJSON() : JSON.parse(JSON.stringify(sub));
        // console.log('Subscription JSON:', subscriptionJSON);
        // console.log('Subscription keys:', subscriptionJSON.keys ? 'Present' : 'Missing', subscriptionJSON.keys);
        
        // Always sync subscription with server to ensure keys are stored
        // This is important because keys might not be in the database
        try {
          const res = await fetch(`${config.API_URL}api/subscribe`, {
            method: "POST",
            headers: { 
              "Content-Type": "application/json"
            },
            credentials: 'include',
            body: JSON.stringify({ subscription: subscriptionJSON, userId: authData.userId })
          });
          await res.json();
          // console.log("Subscription synced with server:", result);
        } catch (err) {
          console.error("Error syncing subscription:", err);
        }
      }
      } catch (err) {
        console.error("Failed to register service worker/push subscription", err);
      }
    } else {
      console.warn("Push notifications are not supported");
    }
  }
  useEffect(() => {if (authData.token) {
    registerAndSubscribe();
  }}, [authData]);

  const notificationSupported = typeof Notification !== 'undefined';
  const [permission, setPermission] = useState(() => (
    notificationSupported ? Notification.permission : 'unsupported'
  ));
  const [showDialog, setShowDialog] = useState(false);
  const [incomingMessagePopup, setIncomingMessagePopup] = useState({ show: false, message: '' });

  // Listen for push notifications from service worker
  useEffect(() => {
    if (isNativeApp()) return;
    if (!authData.token || Number(authData.userTypeId) === MEDIA_MANAGER_ROLE) return;

    const handleMessage = (event) => {
      console.log('App: Message received from service worker', event.data);
      if (event.data && event.data.type === 'PUSH_NOTIFICATION') {
        const notificationData = event.data.payload;
        console.log('App: Showing popup with message', notificationData);
        // Show popup on screen
        setIncomingMessagePopup({
          show: true,
          message: notificationData.body || notificationData.message || 'You have a new message'
        });
      }
    };

    // Listen for messages from service worker
    if ('serviceWorker' in navigator) {
      // Set up message listener
      navigator.serviceWorker.addEventListener('message', handleMessage);
      
      // Also check if service worker is already ready
      navigator.serviceWorker.ready.then((registration) => {
        // conagensole.log('App: Service worker ready', registration);
      }).catch(err => {
        console.error('App: Service worker ready error', err);
      });
    }

    return () => {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', handleMessage);
      }
    };
  }, [authData.token, authData.userTypeId]);

  useEffect(() => {
    if (isNativeApp()) {
      setShowDialog(false);
      return;
    }
    if (!notificationSupported) {
      setShowDialog(false);
      return;
    }
    if (Number(authData.userTypeId) === MEDIA_MANAGER_ROLE) {
      setShowDialog(false);
      return;
    }
    if (permission === "default") {
      // User has not yet chosen
      setShowDialog(true);
    } else if (permission === "denied") {
      console.warn("User has blocked notifications in the browser");
    } else {
      setShowDialog(false);
    }
  }, [permission, notificationSupported, authData.userTypeId]);

  async function handleAllow() {
    setShowDialog(false);
    if (!notificationSupported) {
      console.warn("Notification API is not supported in this browser");
      return;
    }

    const result = await Notification.requestPermission();
    setPermission(result);

    if (result === "granted") {
      console.log("✅ Permission granted");
      await registerAndSubscribe();
    } else {
      console.log("❌ Permission denied");
    }
  }

  return (
    <>
      {showInstallButton && (
        <button
          onClick={handleInstallClick}
          style={{
            position: 'fixed',
            bottom: 20,
            right: '50%',
            transform: 'translateX(50%)',
            padding: '10px 30px',
            backgroundColor: 'rgb(36,47,79)',
            fontFamily: '"ABeeZee", sans-serif',
            color: 'white',
            border: 'none',
            borderRadius: 25,
            cursor: 'pointer',
            zIndex: 1000,
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)'
          }}
        >
          Install PJM App
        </button>
      )}
      {showIosInstallBanner && (
        <div style={{
          backgroundColor: '#fff3cd',
          color: '#856404',
          padding: '1em',
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          textAlign: 'center',
          zIndex: 1000,
          boxShadow: '0 -2px 6px rgba(0,0,0,0.1)'
        }}>
          <p style={{ margin: 0, fontSize: '14px' }}>
            To install this app, tap the “Share” icon and then choose “Add to Home Screen”.
          </p>
        </div>
      )}

      {/* {!showInstallButton && <p>The app is ready for use.</p>} */}
      <AuthContext.Provider value={{ authData, login, logout, isAuthenticated }}>
        <TokenRefresh />
        <PhotoProvider>
          <ThemeProvider theme={ themes(customization) }>
            <BrowserRouter>
              <div className="safe-area">
                <ErrorBoundary>
                  <AppRoutes isAuthenticated = {isAuthenticated} />
                </ErrorBoundary>
              </div>
            </BrowserRouter>
          </ThemeProvider>    
        </PhotoProvider>
      </AuthContext.Provider>

      <PushPermissionDialog
        open={showDialog}
        onClose={() => setShowDialog(false)}
        onAllow={handleAllow}
      />

      <Popup
        setup={incomingMessagePopup}
        onClose={() => setIncomingMessagePopup({ show: false, message: '' })}
      />
    </>
  );
}

export default App;
